/*
 * -----------------------------------------------------------------------
 *  <copyright file="import-library-risks.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState } from 'react';
import { useBoolean } from 'react-hanger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Country } from 'country-list';
import Index from '@components/admin/country-list';
import Button from '@components/core/buttons';
import ExpandableGrid from '@components/core/expandable/grid';
import { ImportLibraryInstructionsProps, ImportLibraryProps } from '../interfaces/import-library.interface';
import { YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { useTranslation } from '@hooks/index';
import { NamedItemFields } from '@queries/generated/fragments.generated';

const TabContainer = ({ children }: any) => (
    <Typography component="div" style={{ padding: 24 }}>
        {children}
    </Typography>
);

const LibraryCategory = (dataItem: [string], lovs: NamedItemFields[]): string =>
    dataItem.map((id) => lovs?.find((lov: any) => lov.id === id)?.name).join(', ');

const LibraryVendor = (dataItem: [string], lovs: NamedItemFields[]): string =>
    dataItem.map((id) => lovs?.find((lov: any) => lov.id === id)?.name).join(', ');

const getCommaSeparatedNames = (items: Country[]): string => items.map((item) => item.name).join(', ');

export const ImportLibraryRisks = ({ data, lovs, isSystemLevel }: ImportLibraryProps) => {
    const { t } = useTranslation();

    if (!data || !data.length) return <div>{t('ra:importLibraryRisksNoData')}</div>;
    return (
        <>
            <div className="secondary" style={{ fontSize: 14, marginBottom: 20 }}>
                {t('ra:confirmImportedLibraryRisks', { count: data.length })}
            </div>
            <ExpandableGrid
                customKey="imported-library-risks-grid"
                id="imported-library-risks"
                key="imported-library-risks-grid"
                items={data}
                groupable={false}
                showPager
                scrollable="scrollable"
                reorderable
                pageable={false}
                resizable
                removeHideFilter
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                columns={[
                    {
                        field: 'statement',
                        label: t('ra:statement'),
                        width: 550,
                    },
                    ...(!isSystemLevel
                        ? [
                              {
                                  field: 'responsibleFunctionId',
                                  label: t('ra:responsibleFunction'),
                                  width: 220,
                                  cells: {
                                      data: ({ dataItem }: any) => (
                                          <td>
                                              {
                                                  lovs?.find((lov: any) => lov.id === dataItem.responsibleFunctionId)
                                                      ?.name
                                              }
                                          </td>
                                      ),
                                  },
                              },
                              {
                                  field: 'controlStrategyId',
                                  label: t('ra:controlStrategy'),
                                  width: 220,
                                  cells: {
                                      data: ({ dataItem }: any) => (
                                          <td>
                                              {lovs?.find((lov: any) => lov.id === dataItem.controlStrategyId)?.name}
                                          </td>
                                      ),
                                  },
                              },
                              {
                                  field: 'riskCategoryId',
                                  label: t('ra:category'),
                                  width: 220,
                                  cells: {
                                      data: ({ dataItem }: any) => (
                                          <td>{lovs?.find((lov: any) => lov.id === dataItem.riskCategoryId)?.name}</td>
                                      ),
                                  },
                              },
                              {
                                  field: 'riskLibraryCategoryIds',
                                  label: t('ra:libraryCategory'),
                                  width: 220,
                                  cells: {
                                      data: ({ dataItem }: any) => (
                                          <td>{LibraryCategory(dataItem.riskLibraryCategoryIds, lovs)}</td>
                                      ),
                                  },
                              },
                              {
                                  field: 'reviewRequired',
                                  label: t('ra:reviewRequired'),
                                  width: 250,
                                  cells: { data: YesNoCell },
                              },
                              {
                                  field: 'reviewFrequencyPeriod',
                                  label: t('ra:reviewFrequencyUnit'),
                                  width: 250,
                              },
                              {
                                  field: 'reviewFrequencyUnits',
                                  label: t('ra:reviewFrequencyPeriod'),
                                  width: 250,
                              },
                              {
                                  field: 'riskLibraryVendorIds',
                                  label: t('ra:vendors'),
                                  width: 220,
                                  cells: {
                                      data: ({ dataItem }: any) => <td>{LibraryVendor(dataItem.vendorIds, lovs)}</td>,
                                  },
                              },
                              {
                                  field: 'countries',
                                  label: t('ra:countries'),
                                  width: 220,
                                  cells: {
                                      data: ({ dataItem }: any) => (
                                          <td>{getCommaSeparatedNames(dataItem.countries)}</td>
                                      ),
                                  },
                              },
                          ]
                        : []),
                ]}
                showFirstColumn={false}
            />
        </>
    );
};

export const ImportLibraryRisksInstructions = ({ isSystemLevel }: ImportLibraryInstructionsProps) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(0);
    const showCountryListDialog = useBoolean(false);
    return (
        <>
            <div className="secondary" style={{ fontSize: 14, marginBottom: 15 }}>
                {t('ra:importLibraryRisksInstructions')}
            </div>
            <Grid
                style={{ fontSize: 11 }}
                data={[
                    {
                        number: 1,
                        field: t('ra:statement'),
                        type: 'Text',
                        maxLength: 600,
                        required: t('yes'),
                        notes: t('ra:n/a'),
                    },
                    ...(!isSystemLevel
                        ? [
                              {
                                  number: 2,
                                  field: t('ra:responsibleFunction'),
                                  maxLength: 70,
                                  required: t('no'),
                                  type: 'Text',
                                  notes: t('ra:activeLovForOrg'),
                              },
                              {
                                  number: 3,
                                  field: t('ra:controlStrategy'),
                                  maxLength: 70,
                                  required: t('no'),
                                  type: 'Text',
                                  notes: t('ra:activeLovForOrg'),
                              },
                              {
                                  number: 4,
                                  field: t('ra:category'),
                                  maxLength: 70,
                                  required: t('no'),
                                  type: 'Text',
                                  notes: t('ra:activeLovForOrg'),
                              },
                              {
                                  number: 5,
                                  field: t('ra:libraryCategory'),
                                  maxLength: 70,
                                  required: t('no'),
                                  type: 'Text',
                                  notes: t('ra:activeLovForOrgCommaSeparated'),
                              },
                              {
                                  number: 6,
                                  field: t('ra:reviewRequired'),
                                  maxLength: t('ra:n/a'),
                                  required: t('no/yes'),
                                  type: 'Boolean',
                                  notes: t('ra:reviewRequiredNotes'),
                              },
                              {
                                  number: 7,
                                  field: t('ra:reviewFrequencyUnit'),
                                  maxLength: t('ra:n/a'),
                                  required: t('no/yes'),
                                  type: 'Number',
                                  notes: t('ra:reviewFrequencyUnitNotes'),
                              },
                              {
                                  number: 8,
                                  field: t('ra:reviewFrequencyPeriod'),
                                  maxLength: t('ra:n/a'),
                                  required: t('no/yes'),
                                  type: 'Text',
                                  notes: t('ra:reviewFrequencyPeriodNotes'),
                              },
                              {
                                  number: 9,
                                  field: t('ra:vendors'),
                                  maxLength: t('ra:n/a'),
                                  required: t('no'),
                                  type: 'Text',
                                  notes: t('ra:activeLovForOrg'),
                              },
                              {
                                  number: 10,
                                  field: t('ra:countries'),
                                  maxLength: t('ra:n/a'),
                                  required: t('no'),
                                  type: 'Text',
                                  notes: t('ra:countriesNotes'),
                              },
                          ]
                        : []),
                ]}
            >
                <Column title={t('ra:columnNum')} field="number" width={120} />
                <Column title={t('ra:field')} field="field" width={120} />
                <Column title={t('ra:fieldType')} field="type" width={120} />
                <Column title={t('ra:maxLength')} field="maxLength" width={120} />
                <Column title={t('ra:required')} field="required" width={100} />
                <Column title={t('ra:notes')} field="notes" />
            </Grid>
            {!isSystemLevel && (
                <div className="k-d-flex k-justify-content-end k-mt-2">
                    <Button
                        text={t('ra:showCountryList')}
                        id="show-country-list-dialog-button"
                        data-testid="show-country-list-dialog-button"
                        onClick={() => {
                            showCountryListDialog.setTrue();
                        }}
                    />
                </div>
            )}
            {showCountryListDialog.value && (
                <Index
                    onClose={() => {
                        showCountryListDialog.setFalse();
                    }}
                />
            )}

            <br />
            <br />
            <h3>{t('ra:Examples')}:</h3>
            <Tabs
                value={value}
                onChange={(e, newValue) => setValue(newValue)}
                indicatorColor="primary"
                textColor="primary"
                centered
            >
                <Tab label="XLSX" icon={<FontAwesomeIcon icon={['fal', 'file-excel']} />} data-testid="xlsx-tab" />
                <Tab label="CSV" icon={<FontAwesomeIcon icon={['fal', 'file-csv']} />} data-testid="csv-tab" />
                <Tab label="PSV" icon={<FontAwesomeIcon icon={['fal', 'file-spreadsheet']} />} data-testid="psv-tab" />
            </Tabs>
            {value === 0 && (
                <TabContainer>
                    <img
                        src={
                            isSystemLevel
                                ? '/img/import-library-risks-example-xlsx.png'
                                : '/img/import-library-org-risks-example-xlsx.PNG'
                        }
                        alt="XLSX"
                        className="import-image-example"
                    />
                </TabContainer>
            )}
            {value === 1 && (
                <TabContainer>
                    <img
                        src={
                            isSystemLevel
                                ? '/img/import-library-risks-example-csv.png'
                                : '/img/import-library-org-risks-example-csv.PNG'
                        }
                        alt="CSV"
                        className="import-image-example"
                    />
                </TabContainer>
            )}
            {value === 2 && (
                <TabContainer>
                    <img
                        src={
                            isSystemLevel
                                ? '/img/import-library-risks-example-psv.png'
                                : '/img/import-library-org-risks-example-psv.PNG'
                        }
                        alt="PSV"
                        className="import-image-example"
                    />
                </TabContainer>
            )}
        </>
    );
};
